import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Crown, DoorOpen, KeyRound, Pencil, Plus, TreePine, Trash2, Users, X } from 'lucide-react';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ErrorMessage } from '@/components/common/ErrorMessage';
import { useConfirm } from '@/components/common/ConfirmDialog';
import {
  listTrees,
  createTree,
  updateTree,
  deleteTree,
  setTreeOwner,
  type Tree,
} from '@/api/trees.api';
import { getUsers } from '@/api/users.api';
import {
  beendeZugang,
  fetchZugaenge,
  nimmZugang,
  type Betriebszugang,
} from '@/api/sicherheit.api';

// ─── AdminTreesPage ─────────────────────────────────────────────────────────
//
// Verwaltung der Stammbäume (nur Admins). Jeder Baum ist ein eigener
// Datenraum; Zugriff erhalten Nutzer über Rollen JE Stammbaum in der
// Benutzerverwaltung.

export function AdminTreesPage() {
  const queryClient = useQueryClient();
  const confirm = useConfirm();

  const [editing, setEditing] = useState<'new' | Tree | null>(null);
  const [besitzWechsel, setBesitzWechsel] = useState<Tree | null>(null);
  const [zugriff, setZugriff] = useState<Tree | null>(null);

  // Laufende Betriebszugänge, damit die Zeile zeigt, wo gerade einer offen ist.
  const { data: zugaenge } = useQuery({
    queryKey: ['admin', 'tree-access', 'aktiv'],
    queryFn: () => fetchZugaenge(true),
  });
  const zugangJeBaum = new Map((zugaenge?.data ?? []).map((z) => [z.treeId, z]));

  const { data: trees, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['trees', 'accessible'],
    queryFn: listTrees,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['trees'] });

  const saveMutation = useMutation({
    mutationFn: ({ id, input }: { id: string | null; input: { name: string; description: string | null } }) =>
      id ? updateTree(id, input) : createTree(input),
    onSuccess: (_data, vars) => {
      invalidate();
      toast.success(vars.id ? 'Stammbaum aktualisiert.' : 'Stammbaum angelegt.');
      setEditing(null);
    },
    onError: (err: Error) => toast.error(err.message || 'Speichern fehlgeschlagen.'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteTree(id),
    onSuccess: () => {
      invalidate();
      toast.success('Stammbaum gelöscht.');
    },
    onError: (err: Error) => toast.error(err.message || 'Löschen fehlgeschlagen.'),
  });

  const handleDelete = async (tree: Tree) => {
    const ok = await confirm({
      title: 'Stammbaum löschen?',
      message: (
        <>
          <strong>„{tree.name}"</strong> wird mit <strong>allen {tree.personCount} Personen</strong>,
          Beziehungen, Medien und Quellen unwiderruflich gelöscht. Erstelle vorher ein Backup, wenn
          du die Daten behalten möchtest.
        </>
      ),
      confirmLabel: 'Endgültig löschen',
      variant: 'danger',
    });
    if (ok) deleteMutation.mutate(tree.id);
  };

  return (
    <>
      <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6">
        <header className="mb-6">
          <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-900">
            <TreePine className="h-6 w-6 text-emerald-600" aria-hidden="true" />
            Stammbäume
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Jeder Stammbaum ist ein eigener Datenraum mit eigenen Personen, Medien und Auswertungen.
            Zugriff erhalten Nutzer über Rollen je Stammbaum in der Benutzerverwaltung. Backups
            umfassen immer alle Stammbäume.
          </p>
        </header>

        <div className="mb-4 flex justify-end">
          <button
            onClick={() => setEditing('new')}
            className="flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            Stammbaum
          </button>
        </div>

        {isLoading && (
          <div className="flex justify-center py-12">
            <LoadingSpinner message="Stammbäume werden geladen..." />
          </div>
        )}

        {isError && (
          <ErrorMessage
            message={error instanceof Error ? error.message : 'Laden fehlgeschlagen.'}
            onRetry={() => refetch()}
          />
        )}

        {trees && (
          <ul className="space-y-3">
            {trees.map((tree) => {
              // Kein „aktiver" Baum in der Konsole: hier wird nicht in einem
              // Stammbaum gearbeitet, sondern über alle hinweg verwaltet.
              const isLast = trees.length <= 1;
              return (
                <li
                  key={tree.id}
                  className="flex items-start justify-between gap-3 rounded-lg border border-slate-200 bg-white p-4 shadow-sm"
                >
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="font-semibold text-slate-900">{tree.name}</h2>
                    </div>
                    {tree.description && (
                      <p className="mt-1 text-sm text-slate-600">{tree.description}</p>
                    )}
                    <p className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-400">
                      <span className="flex items-center gap-1.5">
                        <Users className="h-3.5 w-3.5" aria-hidden="true" />
                        {tree.personCount} {tree.personCount === 1 ? 'Person' : 'Personen'}
                      </span>
                      <span>
                        {tree.memberCount} {tree.memberCount === 1 ? 'Mitglied' : 'Mitglieder'}
                      </span>
                    </p>

                    {/* Wem gehört der Baum? Ohne Besitzer kann niemand seine
                        Mitglieder verwalten – das muss auffallen. */}
                    <p className="mt-1.5 flex items-center gap-1.5 text-xs">
                      <Crown className="h-3.5 w-3.5 shrink-0 text-amber-500" aria-hidden="true" />
                      {tree.owner ? (
                        <span className="text-slate-600">{tree.owner.email}</span>
                      ) : (
                        <span className="font-medium text-red-700">
                          Kein Besitzer – niemand kann Mitglieder verwalten
                        </span>
                      )}
                    </p>
                  </div>

                  <div className="flex shrink-0 gap-1">
                    <button
                      onClick={() => setZugriff(tree)}
                      className={`rounded p-2 ${
                        zugangJeBaum.has(tree.id)
                          ? 'text-amber-600 hover:bg-amber-50'
                          : 'text-slate-400 hover:bg-slate-100 hover:text-slate-600'
                      }`}
                      aria-label={`Zugriff auf ${tree.name} nehmen`}
                      title={
                        zugangJeBaum.has(tree.id)
                          ? 'Zugriff läuft – anklicken zum Beenden oder Verlängern'
                          : 'Befristeten Zugriff nehmen (mit Grund, wird protokolliert)'
                      }
                    >
                      {zugangJeBaum.has(tree.id) ? (
                        <DoorOpen className="h-4 w-4" aria-hidden="true" />
                      ) : (
                        <KeyRound className="h-4 w-4" aria-hidden="true" />
                      )}
                    </button>
                    <button
                      onClick={() => setBesitzWechsel(tree)}
                      className="rounded p-2 text-slate-400 hover:bg-amber-50 hover:text-amber-600"
                      aria-label={`Besitzer von ${tree.name} zuweisen`}
                      title="Besitzer zuweisen"
                    >
                      <Crown className="h-4 w-4" aria-hidden="true" />
                    </button>
                    <button
                      onClick={() => setEditing(tree)}
                      className="rounded p-2 text-slate-400 hover:bg-blue-50 hover:text-blue-600"
                      aria-label={`${tree.name} bearbeiten`}
                      title="Bearbeiten"
                    >
                      <Pencil className="h-4 w-4" aria-hidden="true" />
                    </button>
                    {!isLast && (
                      <button
                        onClick={() => handleDelete(tree)}
                        disabled={deleteMutation.isPending}
                        className="rounded p-2 text-slate-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                        aria-label={`${tree.name} löschen`}
                        title="Löschen"
                      >
                        <Trash2 className="h-4 w-4" aria-hidden="true" />
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {trees && trees.length === 1 && (
          <p className="mt-4 text-xs text-slate-400">
            Der letzte verbleibende Stammbaum kann nicht gelöscht werden.
          </p>
        )}
      </div>

      {editing && (
        <TreeFormModal
          tree={editing === 'new' ? null : editing}
          isPending={saveMutation.isPending}
          onSubmit={(input) =>
            saveMutation.mutate({ id: editing === 'new' ? null : editing.id, input })
          }
          onClose={() => setEditing(null)}
        />
      )}

      {zugriff && (
        <ZugriffModal
          baum={zugriff}
          laufend={zugriff ? zugangJeBaum.get(zugriff.id) ?? null : null}
          maxStunden={zugaenge?.maxStunden ?? 72}
          onClose={() => setZugriff(null)}
        />
      )}
      {besitzWechsel && (
        <BesitzerModal
          tree={besitzWechsel}
          onClose={() => setBesitzWechsel(null)}
          onDone={() => {
            setBesitzWechsel(null);
            invalidate();
          }}
        />
      )}
    </>
  );
}

// ─── Formular-Modal ─────────────────────────────────────────────────────────

function TreeFormModal({
  tree,
  isPending,
  onSubmit,
  onClose,
}: {
  tree: Tree | null;
  isPending: boolean;
  onSubmit: (input: { name: string; description: string | null }) => void;
  onClose: () => void;
}) {
  const [name, setName] = useState(tree?.name ?? '');
  const [description, setDescription] = useState(tree?.description ?? '');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Name ist erforderlich.');
      return;
    }
    onSubmit({ name: name.trim(), description: description.trim() || null });
  };

  const inputClass =
    'w-full rounded border border-slate-300 px-2.5 py-1.5 text-sm text-slate-900 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400';

  return (
    <div className="fixed inset-0 z-[1400] flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-lg bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <h2 className="text-lg font-semibold text-slate-900">
            {tree ? 'Stammbaum bearbeiten' : 'Neuer Stammbaum'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-slate-400 hover:bg-slate-100"
            aria-label="Schließen"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 px-5 py-4">
          <div>
            <label htmlFor="tree-name" className="mb-1 block text-sm font-medium text-slate-700">
              Name *
            </label>
            <input
              id="tree-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="z. B. Familie Müller"
              className={inputClass}
              autoFocus
            />
          </div>
          <div>
            <label htmlFor="tree-desc" className="mb-1 block text-sm font-medium text-slate-700">
              Beschreibung
            </label>
            <textarea
              id="tree-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className={inputClass}
            />
          </div>

          <div className="flex items-center justify-end gap-3 border-t border-slate-200 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:opacity-50"
            >
              {isPending ? 'Speichern…' : 'Speichern'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Befristeter Zugriff auf einen fremden Stammbaum ────────────────────────
//
// Bis vor kurzem schloss die Rolle „Betreiber" jeden Baum auf – ohne Grund,
// ohne Ablauf, ohne Spur. Jetzt braucht es diesen Schritt, und er kostet zwei
// Angaben: warum, und wie lange.
//
// Der Grund landet im Protokoll. Er ist nicht Bürokratie, sondern das, was
// der Besitzer später lesen kann, wenn er wissen will, wer in seinen Daten war.
//
// Gesundheitsdaten bleiben auch mit Zugang gesperrt. Ein Betriebszugang ist
// eine betriebliche Notwendigkeit, keine Einwilligung nach Art. 9 DSGVO.

function ZugriffModal({
  baum,
  laufend,
  maxStunden,
  onClose,
}: {
  baum: Tree | null;
  laufend: Betriebszugang | null;
  maxStunden: number;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [grund, setGrund] = useState(laufend?.reason ?? '');
  const [stunden, setStunden] = useState('24');

  const aktualisieren = () => {
    queryClient.invalidateQueries({ queryKey: ['admin', 'tree-access'] });
    queryClient.invalidateQueries({ queryKey: ['admin', 'security'] });
  };

  const nehmen = useMutation({
    mutationFn: () =>
      nimmZugang({ treeId: baum!.id, reason: grund.trim(), stunden: Number(stunden) }),
    onSuccess: (r) => {
      aktualisieren();
      toast.success(`Zugriff bis ${new Date(r.expiresAt).toLocaleString('de-DE')}.`);
      onClose();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const beenden = useMutation({
    mutationFn: () => beendeZugang(laufend!.id),
    onSuccess: () => {
      aktualisieren();
      toast.success('Zugriff beendet.');
      onClose();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (!baum) return null;

  const grundZuKurz = grund.trim().length < 5;
  const dauerUngueltig =
    !Number.isInteger(Number(stunden)) || Number(stunden) < 1 || Number(stunden) > maxStunden;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-slate-900/40 p-0 sm:items-center sm:p-4">
      <div className="w-full max-w-lg rounded-t-xl bg-white p-5 shadow-xl sm:rounded-xl">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold text-slate-900">
              Zugriff auf „{baum.name}"
            </h2>
            <p className="mt-0.5 text-sm text-slate-500">
              {baum.owner ? `Gehört ${baum.owner.email}` : 'Ohne Besitzer'}
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Schließen"
            className="rounded-md p-1 text-slate-400 hover:bg-slate-100"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        {laufend && (
          <p className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            Ein Zugriff läuft bereits – bis{' '}
            {new Date(laufend.expiresAt).toLocaleString('de-DE')}. Grund: „{laufend.reason}"
          </p>
        )}

        <div className="mt-4 space-y-4">
          <div>
            <label htmlFor="zugriff-grund" className="mb-1 block text-xs font-medium text-slate-700">
              Grund
            </label>
            <textarea
              id="zugriff-grund"
              rows={2}
              value={grund}
              onChange={(e) => setGrund(e.target.value)}
              placeholder="z. B. Anfrage des Besitzers: Import schlägt fehl"
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
            />
            <p className="mt-1 text-xs text-slate-400">
              Steht so im Protokoll und ist für den Besitzer lesbar.
            </p>
          </div>

          <div>
            <label htmlFor="zugriff-dauer" className="mb-1 block text-xs font-medium text-slate-700">
              Dauer (Stunden)
            </label>
            <input
              id="zugriff-dauer"
              type="number"
              min={1}
              max={maxStunden}
              value={stunden}
              onChange={(e) => setStunden(e.target.value)}
              className="w-28 rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
            />
            <p className="mt-1 text-xs text-slate-400">
              Höchstens {maxStunden} Stunden. Danach endet der Zugriff von selbst.
            </p>
          </div>

          <p className="rounded-md bg-slate-50 p-3 text-xs leading-relaxed text-slate-500">
            Der Stammbaum erscheint danach in Ihrer Anwendung wie ein eigener.
            Gesundheitsdaten bleiben gesperrt – dafür braucht es eine Mitgliedschaft
            mit ausdrücklicher Berechtigung.
          </p>
        </div>

        <div className="mt-5 flex flex-wrap justify-end gap-2">
          {laufend && (
            <button
              onClick={() => beenden.mutate()}
              disabled={beenden.isPending}
              className="rounded-md border border-red-200 px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
            >
              Zugriff beenden
            </button>
          )}
          <button
            onClick={onClose}
            className="rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Abbrechen
          </button>
          <button
            onClick={() => nehmen.mutate()}
            disabled={nehmen.isPending || grundZuKurz || dauerUngueltig}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {laufend ? 'Verlängern' : 'Zugriff nehmen'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Besitzer zuweisen ──────────────────────────────────────────────────────
//
// Eingriff des Betreibers, nicht der Normalweg. Im Regelfall übergibt der
// Besitzer seinen Stammbaum selbst in der Anwendung. Hier geht es um Bäume,
// an die sonst niemand mehr herankommt.

function BesitzerModal({
  tree,
  onClose,
  onDone,
}: {
  tree: Tree;
  onClose: () => void;
  onDone: () => void;
}) {
  const [userId, setUserId] = useState(tree.owner?.userId ?? '');
  const [suche, setSuche] = useState('');

  const { data: users, isLoading } = useQuery({ queryKey: ['users'], queryFn: getUsers });

  // Deaktivierte Konten können keinen Baum besitzen – der Server weist sie ab,
  // also stehen sie hier gar nicht erst zur Wahl.
  const auswahl = (users ?? [])
    .filter((u) => u.isActive)
    .filter((u) => u.email.toLowerCase().includes(suche.trim().toLowerCase()));

  const mutation = useMutation({
    mutationFn: () => setTreeOwner(tree.id, userId),
    onSuccess: () => {
      toast.success('Besitzer zugewiesen.');
      onDone();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div
      className="fixed inset-0 z-[1400] flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="flex max-h-[90vh] w-full max-w-md flex-col rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-slate-200 px-5 py-4">
          <h2 className="text-lg font-semibold text-slate-900">Besitzer zuweisen</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-slate-400 hover:bg-slate-100"
            aria-label="Schließen"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!userId) {
              toast.error('Bitte ein Konto wählen.');
              return;
            }
            mutation.mutate();
          }}
          className="flex min-h-0 flex-1 flex-col"
        >
          <div className="shrink-0 px-5 pt-4">
            <p className="text-sm leading-relaxed text-slate-600">
              „{tree.name}" geht an das gewählte Konto. Es übernimmt damit die
              Mitgliederverwaltung und alle Rechte am Baum außer Gesundheitsdaten.
              {tree.owner && (
                <>
                  {' '}
                  Bisher: <strong>{tree.owner.email}</strong> – dieses Konto bleibt
                  Mitglied.
                </>
              )}
            </p>

            <label htmlFor="besitzer-suche" className="mt-4 mb-1 block text-sm font-medium text-slate-700">
              Konto
            </label>
            <input
              id="besitzer-suche"
              value={suche}
              onChange={(e) => setSuche(e.target.value)}
              placeholder="E-Mail suchen…"
              autoFocus
              className="w-full rounded border border-slate-300 px-2.5 py-1.5 text-sm text-slate-900 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
            />
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-3">
            {isLoading && <p className="text-sm text-slate-500">Konten werden geladen…</p>}
            {!isLoading && auswahl.length === 0 && (
              <p className="text-sm text-slate-500">Kein passendes Konto.</p>
            )}
            <ul className="space-y-1">
              {auswahl.map((u) => (
                <li key={u.id}>
                  <label className="flex cursor-pointer items-center gap-2.5 rounded px-2 py-1.5 text-sm hover:bg-slate-50">
                    <input
                      type="radio"
                      name="besitzer"
                      value={u.id}
                      checked={userId === u.id}
                      onChange={() => setUserId(u.id)}
                    />
                    <span className="min-w-0 flex-1 truncate text-slate-800">{u.email}</span>
                    {u.id === tree.owner?.userId && (
                      <span className="shrink-0 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] text-amber-700">
                        aktuell
                      </span>
                    )}
                    {u.role === 'admin' && (
                      <span className="shrink-0 rounded-full bg-purple-50 px-2 py-0.5 text-[11px] text-purple-700">
                        Betreiber
                      </span>
                    )}
                  </label>
                </li>
              ))}
            </ul>
          </div>

          <div className="flex shrink-0 items-center justify-end gap-3 border-t border-slate-200 px-5 py-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={mutation.isPending || !userId || userId === tree.owner?.userId}
              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:opacity-50"
            >
              {mutation.isPending ? 'Wird zugewiesen…' : 'Zuweisen'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AdminTreesPage;
